import type { CapiPlp } from 'types/capi/plp';

const MAIN_LIST_MODULE_TYPE = 'plp-main-list';
const POPULAR_CAROUSEL_MODULE_TYPE = 'plp-popular-carousel';
const POPULAR_CAROUSEL_MODULE_POSITION = 1;

type PlpViewProperties = {
  filters?: { key: string; value: string }[];
  items: {
    module_position: number;
    module_type: string;
    position: number;
    product_id: string;
  }[];
  page: number;
  page_size?: number;
  pagination_mode: 'paged' | 'scroll';
  result_count?: number;
  search_term?: string;
  sort_key?: string;
};

// product_type is excluded: it describes the rendered surface, not a filter the
// user applied.
//
// This is NOT the whole picture of whether a list is narrowed, and must not be
// read as one. It is built from applied_filters, which only knows about the
// facet menus. The top-bar chips narrow through the sort mechanism instead —
// DA resolves SALE to has_sale_price:true and it never reaches applied_filters
// — so a sale-only PLP reports no filters and is indistinguishable here from an
// unfiltered one. Deriving "was this list narrowed" needs sort_key too,
// resolved against what each key actually does, and that belongs downstream.
export function getFilters(plp: CapiPlp) {
  const filters = Object.entries(plp.applied_filters ?? {})
    .filter(([key, value]) => key !== 'product_type' && value != null)
    .map(([key, value]) => ({ key, value: String(value) }))
    .sort((a, b) => a.key.localeCompare(b.key));

  return filters.length ? filters : undefined;
}

// 1-based ordinal from the ?page= param; anything absent or malformed is the
// first page.
function getPage(params: URLSearchParams) {
  const page = Number(params.get('page'));

  return Number.isInteger(page) && page >= 1 ? page : 1;
}

export function getPlpViewProperties(plp: CapiPlp): PlpViewProperties {
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const params = new URLSearchParams(search);

  return {
    filters: getFilters(plp),
    items: [
      ...(plp.popular_products ?? []).map((product, index) => ({
        module_position: POPULAR_CAROUSEL_MODULE_POSITION,
        module_type: POPULAR_CAROUSEL_MODULE_TYPE,
        position: index + 1,
        product_id: String(product.id)
      })),
      ...plp.products.map((product, index) => ({
        module_position: plp.popular_products
          ? POPULAR_CAROUSEL_MODULE_POSITION + 1
          : POPULAR_CAROUSEL_MODULE_POSITION,
        module_type: MAIN_LIST_MODULE_TYPE,
        position: index + 1,
        product_id: String(product.id)
      }))
    ],
    page: getPage(params),
    page_size: plp.page_size,
    pagination_mode: 'paged',
    result_count: plp.result_count,
    search_term: params.get('q') ?? undefined,
    sort_key: plp.applied_sort_key
  };
}
